import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BoxHelper, BufferAttribute, BufferGeometry, type Mesh } from 'three';
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js';
import { useShallow } from 'zustand/shallow';

import { convertToFloatArray } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    filterByStorey,
    getActiveStorey
} from 'app/modules/wallforming/components/scenes/objects/building/utils/storeyUtils';
import { createPois } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { BuildingModel } from 'api/models';

interface BuildingMeshProps {
    buildingModel: BuildingModel;
}

export const BuildingMesh = ({ buildingModel }: BuildingMeshProps) => {
    const {
        shadowsEnabled,
        buildingHelperRef,
        mainCameraControlsRef,
        setPois,
        setColliders,
        fitToObject,
        setBuildingHelperRef,
        setBuildingWallEdges,
        saveMainCameraView,
    restoreMainCameraView,
        setBuildingStoreys,
        selectedStoreyGuid
    } = usePlacementStore(
        useShallow((state) => ({
            buildingHelperRef: state.buildingHelperRef,
            mainCameraControlsRef: state.mainCameraControlsRef,
            setPois: state.setPois,
            setColliders: state.setColliders,
            fitToObject: state.fitToObject,
            setBuildingHelperRef: state.setBuildingHelperRef,
            setBuildingWallEdges: state.setBuildingWallEdges,
            saveMainCameraView: state.saveMainCameraView,
            restoreMainCameraView: state.restoreMainCameraView,
            shadowsEnabled: state.graphicsSettings.shadowsEnabled,
            setBuildingStoreys: state.setBuildingStoreys,
            selectedStoreyGuid: state.selectedStoreyGuid
        }))
    );

    const gl = useThree((state) => state.gl);
    const buildingGuid = buildingModel.guid;
    const buildingMeshRef = useRef<Mesh | null>(null);

    // Scoping geometry and POIs here keeps every POI consumer (navigation, colliders, solvers, copilot) on one storey.
    const activeStorey = useMemo(
        () => getActiveStorey(buildingModel.storeys, selectedStoreyGuid),
        [buildingModel.storeys, selectedStoreyGuid]
    );
    const faceMeshes = useMemo(
        () => filterByStorey(buildingModel.buildingFaceMesh, activeStorey),
        [activeStorey, buildingModel.buildingFaceMesh]
    );
    const pointsOfInterest = useMemo(
        () => filterByStorey(buildingModel.pointsOfInterest, activeStorey),
        [activeStorey, buildingModel.pointsOfInterest]
    );

    const mergedGeometry = useMemo(() => {
        if (faceMeshes && faceMeshes.length > 0) {
            const geometries = faceMeshes.map((faceMesh) => {
                const geometry = new BufferGeometry();
                geometry.setAttribute('position', new BufferAttribute(convertToFloatArray(faceMesh.vertices), 3));
                geometry.setIndex(faceMesh.indices);
                geometry.computeVertexNormals();
                return geometry;
            });

            return BufferGeometryUtils.mergeGeometries(geometries, false);
        }

        return new BufferGeometry();
    }, [faceMeshes]);

    useEffect(() => {
        return () => {
            mergedGeometry.dispose();
            setBuildingHelperRef(null);
        };
    }, [mergedGeometry, setBuildingHelperRef]);

    useEffect(() => {
        // Models saved before storeys existed have none.
        setBuildingStoreys(buildingModel.storeys ?? []);
        return () => setBuildingStoreys([]);
    }, [buildingModel.storeys, setBuildingStoreys]);

    useEffect(() => {
        const { pois, colliderPoints, wallEdges } = createPois(pointsOfInterest);
        setPois(pois);
        setColliders(colliderPoints, 'building');
        setBuildingWallEdges(wallEdges);
    }, [pointsOfInterest, setBuildingWallEdges, setColliders, setPois]);

    // Keep the camera view across page reloads
    useEffect(() => {
        const handlePageHide = () => saveMainCameraView(buildingGuid);
        const controls = mainCameraControlsRef.current;
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                saveMainCameraView(buildingGuid);
            }
        };

        window.addEventListener('pagehide', handlePageHide);
        controls?.addEventListener('rest', handlePageHide);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            controls?.removeEventListener('rest', handlePageHide);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [buildingGuid, mainCameraControlsRef, saveMainCameraView]);

    const fitCameraToBuilding = useCallback(
        (buildingMesh: Mesh, restoreSavedView = true) => {
            if (!buildingHelperRef.current) {
                setBuildingHelperRef(new BoxHelper(buildingMesh, 0xffff00));
            }

            if (buildingHelperRef.current && gl.domElement && mainCameraControlsRef.current) {
                if (!restoreSavedView || !restoreMainCameraView(buildingGuid, gl.domElement)) {
                    fitToObject(buildingHelperRef.current, mainCameraControlsRef, true, true, gl.domElement);
                }
                mainCameraControlsRef.current.enabled = true;
            }
        },
        [
            buildingGuid,
            buildingHelperRef,
            mainCameraControlsRef,
            fitToObject,
            gl.domElement,
            restoreMainCameraView,
            setBuildingHelperRef
        ]
    );

    const handleBuildingMeshRef = useCallback(
        (buildingMesh: Mesh | null) => {
            buildingMeshRef.current = buildingMesh;
            if (buildingMesh) {
                fitCameraToBuilding(buildingMesh);
            }
        },
        [fitCameraToBuilding]
    );

    // A storey switch swaps the geometry of the mounted mesh, so the helper is rebuilt and the camera re-fitted.
    // Keyed on the storey (not the geometry) so StrictMode's effect replay does not trigger an extra fit.
    const fittedStoreyGuidRef = useRef(activeStorey?.storeyGuid ?? null);
    useEffect(() => {
        const storeyGuid = activeStorey?.storeyGuid ?? null;
        if (fittedStoreyGuidRef.current === storeyGuid) {
            return;
        }

        fittedStoreyGuidRef.current = storeyGuid;
        if (buildingMeshRef.current) {
            setBuildingHelperRef(null);
            fitCameraToBuilding(buildingMeshRef.current, false);
        }
    }, [activeStorey, fitCameraToBuilding, setBuildingHelperRef]);

    if (!mergedGeometry) {
        return null;
    }

    // A storey without faces has no position attribute, which EdgesGeometry cannot handle.
    const hasFaces = mergedGeometry.getAttribute('position') !== undefined;

    return (
        <group>
            <mesh ref={handleBuildingMeshRef} receiveShadow={shadowsEnabled}>
                <primitive attach="geometry" object={mergedGeometry} />
                <meshBasicMaterial color={'#e9ebf2'} />
            </mesh>
            {hasFaces && (
                <lineSegments>
                    <edgesGeometry args={[mergedGeometry, 45]} />
                    <lineBasicMaterial color={'gray'} />
                </lineSegments>
            )}
        </group>
    );
};
